import React, { useEffect, useState } from 'react';
import { StytchLogin, useStytch } from '@stytch/react';
import { Products, OTPMethods, OAuthProviders } from '@stytch/vanilla-js';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '@/modules/auth/ui/AuthProvider';
import { Link } from 'react-router-dom';
import { captureError } from '@/utils/errorHandler';

const REDIRECT_STORAGE_KEY = 'zapt_auth_redirect';

export default function AuthenticatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, authFetch } = useAuthContext();
  const stytch = useStytch();
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Get redirect parameter (for quick create flow, etc.)
  const redirectTo = searchParams.get('redirect');

  // Save redirect to sessionStorage on mount (for OAuth flows that lose URL params)
  useEffect(() => {
    if (redirectTo) {
      sessionStorage.setItem(REDIRECT_STORAGE_KEY, redirectTo);
    }
  }, [redirectTo]);

  // Check if Stytch is initialized
  useEffect(() => {
    if (stytch) {
      setIsInitialized(true);
    }
  }, [stytch]);

  // Handle OAuth callback - process token from URL when redirected back from OAuth provider
  useEffect(() => {
    if (stytch && isInitialized) {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      const stytch_token_type = urlParams.get('stytch_token_type');
      
      if (token && stytch_token_type === 'oauth') {
        console.log('🔐 [OAuth] Processing OAuth callback with token:', token.substring(0, 20) + '...');
        
        stytch.oauth.authenticate(token, {
          session_duration_minutes: 10080, // 7 days
        }).then((response) => {
          console.log('🔐 [OAuth] ✅ OAuth authentication successful:', response);
          // Clean up URL
          window.history.replaceState({}, document.title, window.location.pathname);
        }).catch((error) => {
          captureError(error, {
            action: 'handleOAuthCallback',
            component: 'AuthenticatePage',
          });
          console.error('🔐 [OAuth] ❌ OAuth authentication failed:', error);
        });
      } else if (urlParams.has('token')) {
        console.log('🔐 [OAuth Debug] Token found but type is:', stytch_token_type);
        console.log('🔐 [OAuth Debug] All params:', Object.fromEntries(urlParams.entries()));
      }
    }
  }, [stytch, isInitialized]);

  // Check subscription and redirect after authentication
  useEffect(() => {
    if (user) {
      checkSubscriptionAndRedirect();
    }
  }, [user]);

  const checkSubscriptionAndRedirect = async () => {
    if (!user) return;

    // Check for redirect - either from URL param or saved in sessionStorage (for OAuth flows)
    const savedRedirect = sessionStorage.getItem(REDIRECT_STORAGE_KEY);
    const finalRedirect = redirectTo || savedRedirect;
    
    // Clear saved redirect after reading
    if (savedRedirect) {
      sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
    }

    // If there's an explicit redirect, use it (e.g. create-app flow)
    if (finalRedirect) {
      navigate(finalRedirect);
      return;
    }

    // New users (no apps yet) go straight to the brief form instead of an empty dashboard
    try {
      const response = await authFetch('/api/apps');
      if (response.ok) {
        const apps = await response.json();
        if (!apps || apps.length === 0) {
          navigate('/app-summary');
          return;
        }
      }
    } catch {
      // If the check fails, fall through to dashboard
    }

    navigate('/dashboard');
  };

  const oauthCallbackUrl = `${window.location.origin}/authenticate`;
  const stytchConfig = {
    products: [Products.otp, Products.oauth],
    otpOptions: {
      methods: [OTPMethods.Email],
      expirationMinutes: 10,
    },
    oauthOptions: {
      providers: [
        { type: OAuthProviders.Google },
        { type: OAuthProviders.Apple },
      ],
      loginRedirectURL: oauthCallbackUrl,
      signupRedirectURL: oauthCallbackUrl,
    },
    sessionOptions: {
      sessionDurationMinutes: 10080, // 7 days
    },
  };

  const customStrings = {
    'login.title': ' ',
  };

  const stytchStyles = {
    container: {
      width: "100%",
      backgroundColor: "transparent",
    },
    inputs: {
      borderRadius: "12px",
      borderColor: "#e5e7eb",
      backgroundColor: "#f9fafb",
      textColor: "#111827",
      fontSize: "16px",
      padding: "16px 20px",
      focusBorderColor: "#763BEA",
      focusBackgroundColor: "#ffffff",
      boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
      focusBoxShadow: "0 0 0 3px rgba(118, 59, 234, 0.1)",
    },
    buttons: {
      primary: {
        backgroundColor: "#CBFF2E",
        textColor: "#060F38",
        borderRadius: "12px",
        fontSize: "16px",
        fontWeight: "600",
        padding: "16px 24px",
        boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
        hover: {
          backgroundColor: "#B8E62A",
          boxShadow: "0 4px 12px 0 rgba(203, 255, 46, 0.3)",
        }
      }
    },
    text: {
      bodyTextColor: "#6b7280",
      linkColor: "#763BEA",
      headingTextColor: "#111827",
      fontSize: "15px",
    }
  };

  return (
    <div className="min-h-screen bg-zapt-navy flex items-center justify-center px-0 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-40"
        style={{ backgroundImage: 'url(https://zapt-cdn.com/dc408fb4-e206-4be6-8677-dfc0f17d99f7/background_image.jpg)' }}
      />
      
      {/* Gradient overlay for text legibility */}
      <div className="absolute inset-0 bg-gradient-to-b from-zapt-navy/60 via-zapt-navy/70 to-zapt-navy/60" />
      
      {/* Accent glows */}
      <div className="absolute top-1/4 -right-48 w-96 h-96 bg-zapt-purple rounded-full blur-3xl opacity-20"></div>
      <div className="absolute bottom-1/4 -left-48 w-96 h-96 bg-zapt-yellow rounded-full blur-3xl opacity-10"></div>

      <div className="w-full sm:max-w-md space-y-4 sm:space-y-8 relative z-10">
        {/* Header */}
        <div className="text-center px-4 sm:px-0">
          <Link to="/" className="inline-flex items-center text-gray-300 hover:text-white transition-colors mb-4 sm:mb-8 text-sm sm:text-base">
            <svg className="w-4 h-4 sm:w-6 sm:h-6 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to home
          </Link>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2 sm:mb-4">
            Welcome to ZAPT
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-4 sm:mb-8">
            Let's get started.
          </p>
        </div>
        
        {/* Auth Form */}
        <div className="bg-white rounded-none sm:rounded-2xl shadow-2xl p-2 sm:p-6 md:p-8 w-full overflow-hidden">
            <StytchLogin
              config={stytchConfig}
              styles={stytchStyles}
              strings={customStrings}
              callbacks={{
                onEvent: ({ type, data }) => {
                  console.log('Auth event:', { type, data });
                },
                onError: (error) => {
                  console.error('Auth error:', error);
                },
              }}
            />
            
            {/* Legal Disclaimer */}
            <div className="mt-4 sm:mt-6 text-center text-xs sm:text-sm text-gray-600 px-2 sm:px-4">
              By signing in, you agree to our{' '}
              <Link to="/terms-of-service" className="text-zapt-purple hover:text-zapt-purple/80 underline">
                Terms of Service
              </Link>
              {' '}and{' '}
              <Link to="/privacy-policy" className="text-zapt-purple hover:text-zapt-purple/80 underline">
                Privacy Policy
              </Link>
            </div>
          </div>
      </div>
    </div>
  );
}
